import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, FileText, ChevronRight, AlertCircle, Filter, RefreshCw, X,
  Calendar, Zap, CreditCard, CheckCircle, DollarSign, Search,
  Mail, Phone, Edit2, Trash2, Download, Printer, FileSpreadsheet
} from 'lucide-react';
import { getAsaasStatistics, getClients, updateAsaasLastInvoices, apiFetch } from '../services/api';
import './SituacaoCobrancas.css';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${day}/${month}/${year}`;
};

const formatDateAPI = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getFirstDayOfMonth = () => {
  const now = new Date();
  return formatDateAPI(new Date(now.getFullYear(), now.getMonth(), 1));
};

const getToday = () => {
  return formatDateAPI(new Date());
};

const emptyStats = {
  RECEIVED: { value: 0, netValue: 0, customers: 0, count: 0 },
  CONFIRMED: { value: 0, netValue: 0, customers: 0, count: 0 },
  PENDING: { value: 0, netValue: 0, customers: 0, count: 0 },
  OVERDUE: { value: 0, netValue: 0, customers: 0, count: 0 },
};

const statusMap = {
  RECEIVED: 'RECEIVED',
  CONFIRMED: 'CONFIRMED',
  PENDING: 'PENDING',
  OVERDUE: 'OVERDUE'
};

const AUTO_SYNC_INTERVAL = 0; // sincronização automática a cada 5 min desligada (estourava a cota do Asaas); use o botão de atualizar
const AUTO_SYNC_ON_MOUNT = false;

export default function SituacaoCobrancas({ onClientesClick, onCobrancasClick }) {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [activeSource, setActiveSource] = useState('asaas');

  const [stats, setStats] = useState(emptyStats);
  const [sourceCounts, setSourceCounts] = useState({ asaas: 0, cpfl: 0 });

  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getToday());
  const [selectedPeriod, setSelectedPeriod] = useState('thisMonth');

  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [billingType, setBillingType] = useState('ALL');
  const [dateFilterType, setDateFilterType] = useState('dueDate');

  const [selectedCard, setSelectedCard] = useState(null);
  const [cobrancas, setCobrancas] = useState([]);
  const [loadingCobrancas, setLoadingCobrancas] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [selectedClientCard, setSelectedClientCard] = useState(null);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  
  const [useCustomPeriod, setUseCustomPeriod] = useState(false);
  const [autoSyncIntervalId, setAutoSyncIntervalId] = useState(null);
  
  const [selectedCobrancas, setSelectedCobrancas] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  const fetchSourceCounts = useCallback(async () => {
    try {
      const { getClientsBySegment } = await import('../services/api');
      const [cpflData, asaasData] = await Promise.all([
        getClientsBySegment('cpfl', 'todos', true),
        getClientsBySegment('asaas', 'todos', true)
      ]);
      setSourceCounts({ cpfl: cpflData?.count || 0, asaas: asaasData?.count || 0 });
    } catch (err) {
      console.error('Erro ao buscar contadores:', err);
      setSourceCounts({ cpfl: 0, asaas: 0 });
    }
  }, []);

  const syncAsaasDataSilent = useCallback(async () => {
    try {
      const response = await apiFetch('/asaas/sync-smart', {
        method: 'POST',
        body: JSON.stringify({ days: 1 })
      });
      if (response.success) {
        console.log(`✅ Sincronização: ${response.totalSynced} atualizadas, ${response.totalRemoved} removidas`);
        await fetchStats();
      }
    } catch (err) {
      console.error(' Erro ao sincronizar:', err);
    }
  }, []);

  const syncAsaasData = useCallback(async () => {
    setSyncing(true);
    setSyncMessage('Sincronizando dados com Asaas...');
    try {
      const result = await updateAsaasLastInvoices();
      if (result.success) {
        setSyncMessage(`✓ ${result.updated} clientes atualizados`);
        setTimeout(() => setSyncMessage(''), 3000);
      } else {
        setSyncMessage('Erro ao sincronizar');
        setTimeout(() => setSyncMessage(''), 3000);
      }
    } catch (err) {
      console.error('Erro ao sincronizar com Asaas:', err);
      setSyncMessage('Erro ao sincronizar');
      setTimeout(() => setSyncMessage(''), 3000);
    } finally {
      setSyncing(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    if (activeSource === 'cpfl') {
      setStats(emptyStats);
      return;
    }

    if (window.fetchStatsInProgress) {
      console.log('⏸️ fetchStats já em andamento, pulando...');
      return;
    }

    window.fetchStatsInProgress = true;
    setLoading(true);

    try {
      const statuses = ['RECEIVED', 'CONFIRMED', 'PENDING', 'OVERDUE'];
      const newStats = {};
      let periodStart, periodEnd;

      if (useCustomPeriod && startDate && endDate) {
        periodStart = startDate;
        periodEnd = endDate;
        console.log('📅 Período personalizado:', periodStart, 'até', periodEnd);
      } else {
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        periodStart = formatDateAPI(firstDayOfMonth);
        periodEnd = formatDateAPI(lastDayOfMonth);

        console.log('📅 Período automático (ESTE MÊS):');
        console.log('   Início:', periodStart);
        console.log('   Fim:', periodEnd);
        console.log('   Data atual:', new Date().toISOString().split('T')[0]);
      }

      const results = await Promise.all(
        statuses.map(async (status) => {
          const params = { status, dateFilterType };
          if (periodStart) params.startDate = periodStart;
          if (periodEnd) params.endDate = periodEnd;
          if (billingType !== 'ALL') params.billingType = billingType;

          console.log(`📡 Enviando para API - ${status}:`, params);

          try {
            const data = await getAsaasStatistics(params);
            console.log(`✅ Resposta ${status}:`, data);
            return {
              status,
              value: data.value || 0,
              netValue: data.netValue || 0,
              customers: data.customerCount || 0,
              count: data.paymentCount || 0
            };
          } catch (err) {
            console.error(` Erro ao buscar ${status}:`, err);
            return { status, value: 0, netValue: 0, customers: 0, count: 0 };
          }
        })
      );
      
      results.forEach(result => {
        newStats[result.status] = {
          value: result.value,
          netValue: result.netValue,
          customers: result.customers,
          count: result.count
        };
      });

      setStats(newStats);
    } catch (error) {
      console.error("❌ Erro ao carregar estatísticas:", error);
    } finally {
      setLoading(false);
      window.fetchStatsInProgress = false;
    }
  }, [activeSource, billingType, useCustomPeriod, startDate, endDate, dateFilterType]);

  const fetchCobrancas = useCallback(async (status) => {
    setLoadingCobrancas(true);
    try {
      const apiStatus = statusMap[status] || status;
      let periodStart, periodEnd;

      if (useCustomPeriod && startDate && endDate) {
        periodStart = startDate;
        periodEnd = endDate;
        console.log('🔍 Buscando cobranças - Período personalizado:', periodStart, 'até', periodEnd);
      } else {
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        periodStart = formatDateAPI(firstDayOfMonth);
        periodEnd = formatDateAPI(lastDayOfMonth);
        console.log('🔍 Buscando cobranças - Período automático:', periodStart, 'até', periodEnd);
      }

      const params = {
        status: apiStatus,
        startDate: periodStart,
        endDate: periodEnd,
        dateFilterType: dateFilterType,
        limit: 2000
      };

      const response = await apiFetch(`/asaas/local-payments?${new URLSearchParams(params)}`);
      const allPayments = response.data || [];

      console.log(`📊 Total do banco: ${allPayments.length} cobranças`);

      const uniquePayments = allPayments.filter((payment, index, self) =>
        index === self.findIndex((p) => p.id === payment.id)
      );

      console.log(`✅ Após remover duplicatas: ${uniquePayments.length} cobranças`);
      setCobrancas(uniquePayments);
    } catch (err) {
      console.error('❌ Erro ao buscar cobranças:', err);
      setCobrancas([]);
    } finally {
      setLoadingCobrancas(false);
    }
  }, [useCustomPeriod, startDate, endDate, dateFilterType]);

  useEffect(() => {
    const initializeData = async () => {
      console.log('🔄 Carregando estatísticas...');
      if (AUTO_SYNC_ON_MOUNT) {
        console.log('🔄 [Startup] Sincronizando dados com Asaas...');
        await syncAsaasDataSilent();
      }
      await fetchStats();
      await fetchSourceCounts();
      console.log('✅ Dados carregados');
    };
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (AUTO_SYNC_INTERVAL > 0) {
      console.log(`⏰ [Auto-Sync] Configurando sincronização automática a cada ${AUTO_SYNC_INTERVAL / 1000 / 60} minutos`);
      const intervalId = setInterval(() => {
        syncAsaasDataSilent();
      }, AUTO_SYNC_INTERVAL);
      setAutoSyncIntervalId(intervalId);
      return () => {
        clearInterval(intervalId);
        console.log('⏰ [Auto-Sync] Intervalo de sincronização cancelado');
      };
    }
  }, [syncAsaasDataSilent]);

  useEffect(() => {
    fetchSourceCounts();
  }, [fetchSourceCounts]);

  useEffect(() => {
    if (selectedCard) {
      if (selectedPeriod !== 'custom') {
        fetchCobrancas(selectedCard);
      }
    }
  }, [selectedCard, startDate, endDate, selectedPeriod, fetchCobrancas]);

  const handlePeriodSelect = (period) => {
    const now = new Date();
    let start = '';
    let end = getToday();
    let useCustom = false;

    switch (period) {
      case 'today': start = getToday(); break;
      case 'thisMonth': start = getFirstDayOfMonth(); useCustom = false; break;
      case 'thisYear': start = `${now.getFullYear()}-01-01`; useCustom = true; break;
      case 'all': start = ''; end = ''; useCustom = true; break;
      case 'custom':
        setUseCustomPeriod(true);
        setSelectedPeriod('custom');
        return;
      default: start = getFirstDayOfMonth();
    }

    setStartDate(start);
    setEndDate(end);
    setUseCustomPeriod(useCustom);
    setSelectedPeriod(period);
    setShowPeriodDropdown(false);
  };

  const getPeriodLabel = () => {
    const formatDisplayDate = (dateStr) => {
      if (!dateStr) return 'Desde o início';
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    };
    if (selectedPeriod === 'custom') {
      if (startDate && endDate) return `${formatDisplayDate(startDate)} até ${formatDisplayDate(endDate)}`;
      return 'Personalizado';
    }
    const labels = { today: 'Hoje', thisMonth: 'Este mês', thisYear: 'Este ano', all: 'Desde o início' };
    return labels[selectedPeriod] || 'Este mês';
  };

  const handleApplyCustomDates = async () => {
    try {
      console.log(' Aplicando datas personalizadas:', startDate, 'até', endDate);
      if (!startDate || !endDate) {
        alert('Por favor, selecione ambas as datas.');
        return;
      }

      setSelectedPeriod('custom');
      setUseCustomPeriod(true);
      await new Promise(resolve => setTimeout(resolve, 100));
      setShowPeriodDropdown(false);
      await fetchStats();
      
      if (selectedCard) {
        await fetchCobrancas(selectedCard);
      }
      console.log('✅ Dados carregados com sucesso para o período personalizado');
    } catch (error) {
      console.error(' Erro ao aplicar datas personalizadas:', error);
    }
  };

  const handleCardClick = async (cardKey) => {
    if (selectedCard === cardKey) {
      setSelectedCard(null);
    } else {
      setSelectedCard(cardKey);
      await fetchCobrancas(cardKey);
    }
  };

  const fetchClientsBySituacao = useCallback(async (situacao) => {
    console.log(`🔍 Buscando clientes com situação: ${situacao}`);
    setLoadingClients(true);
    setClientSearchTerm(''); // Limpa a busca ao carregar novos clientes
    try {
      if (activeSource === 'asaas') {
        const periodStart = useCustomPeriod && startDate ? startDate : getFirstDayOfMonth();
        const periodEnd = useCustomPeriod && endDate ? endDate : formatDateAPI(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
        
        const params = new URLSearchParams({
          situacao: situacao,
          payment_type: 'asaas',
          limit: '1000',
          startDate: periodStart,
          endDate: periodEnd,
          dateFilterType: dateFilterType
        });
        
        const endpoint = `/clients/by-situacao-asaas?${params.toString()}`;
        console.log(`📡 URL da requisição: ${endpoint}`);
        
        const response = await apiFetch(endpoint);
        console.log(`✅ Resposta do backend:`, response);
        
        const clientsData = response.data || response.clients || [];
        console.log(`📦 Total de clientes recebidos: ${clientsData.length}`);
        setClients(clientsData);
      } else {
        const endpoint = `/clients?situacao=${situacao}&payment_type=${activeSource}&limit=1000`;
        console.log(`📡 URL da requisição: ${endpoint}`);
        
        const response = await apiFetch(endpoint);
        console.log(`✅ Resposta do backend:`, response);
        
        const clientsData = response.data || response.clients || [];
        console.log(`📦 Total de clientes recebidos: ${clientsData.length}`);
        setClients(clientsData);
      }
    } catch (err) {
      console.error('❌ Erro ao buscar clientes:', err);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, [activeSource, useCustomPeriod, startDate, endDate, dateFilterType]);

  const handleClientCardClick = (statusKey) => {
    let situacao = '';
    switch (statusKey) {
      case 'RECEIVED': situacao = 'ADIMPLENTE'; break;
      case 'CONFIRMED': situacao = 'ADIMPLENTE'; break;
      case 'PENDING': situacao = 'PENDENTE'; break;
      case 'OVERDUE': situacao = 'INADIMPLENTE'; break;
      default: return;
    }
    setSelectedClientCard(selectedClientCard === statusKey ? null : statusKey);
    fetchClientsBySituacao(situacao);
  };

  const handleFooterClick = (type, cardKey) => {
    if (type === 'clients') {
      handleClientCardClick(cardKey);
    } else {
      handleCardClick(cardKey);
    }
  };

  const handleSourceChange = (source) => {
    setActiveSource(source);
    setSelectedCard(null);
  };

  const handleManualRefresh = async () => {
    setSyncing(true);
    setSyncMessage(' Sincronizando com Asaas...');

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);

      const response = await apiFetch('/asaas/sync-smart', {
        method: 'POST',
        body: JSON.stringify({ days: 7 }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const total = response.totalSynced || 0;
      setSyncMessage(`✓ ${total} pagamentos sincronizados`);

      await fetchStats();
      await fetchSourceCounts();

      if (selectedCard) {
        await fetchCobrancas(selectedCard);
      }

      setTimeout(() => setSyncMessage(''), 4000);
    } catch (err) {
      console.error('Erro ao sincronizar:', err);
      if (err.name === 'AbortError') {
        setSyncMessage('⚠️ Timeout: Tente novamente ou sincronize menos dias');
      } else {
        setSyncMessage('❌ Erro ao sincronizar');
      }
      setTimeout(() => setSyncMessage(''), 4000);
    } finally {
      setSyncing(false);
    }
  };

  const cards = [
    { key: 'RECEIVED', label: 'Recebidas', cls: 'received', desc: 'Cobranças recebidas' },
    { key: 'CONFIRMED', label: 'Confirmadas', cls: 'confirmed', desc: 'Cobranças confirmadas' },
    { key: 'PENDING', label: 'Aguardando pagamento', cls: 'pending', desc: 'Cobranças pendentes' },
    { key: 'OVERDUE', label: 'Atrasadas', cls: 'overdue', desc: 'Cobranças atrasadas' }
  ];

  const filteredCobrancas = cobrancas.filter(c =>
    !searchTerm || 
    (c.customerName && c.customerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (c.description && c.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // ✅ FILTRO DE CLIENTES - GARANTINDO QUE FUNCIONE
  const filteredClients = clients.filter(c => {
    if (!clientSearchTerm || clientSearchTerm.trim() === '') return true;
    const search = clientSearchTerm.toLowerCase().trim();
    return (
      (c.full_name && c.full_name.toLowerCase().includes(search)) ||
      (c.name && c.name.toLowerCase().includes(search)) ||
      (c.email && c.email.toLowerCase().includes(search)) ||
      (c.cpf && c.cpf.includes(search)) ||
      (c.phone && c.phone.includes(search))
    );
  });

  // ✅ EXPORTAR CLIENTES
  const exportClientsToExcel = () => {
    console.log('📊 Exportando clientes filtrados:', filteredClients.length);
    
    const dataToExport = filteredClients.map(c => ({
      'Nome': c.full_name || c.name || '—',
      'Email': c.email || '—',
      'Telefone': c.phone || '—',
      'CPF/CNPJ': c.cpf || '—',
      'Assinaturas Recebidas': fmt(c.total_assinaturas || 0),
      'Cobranças Recebidas': fmt(c.total_cobrancas || 0),
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clientes');

    const statusName = selectedClientCard === 'RECEIVED' ? 'Adimplentes' :
                       selectedClientCard === 'CONFIRMED' ? 'Confirmados' :
                       selectedClientCard === 'PENDING' ? 'Pendentes' : 'Inadimplentes';
    
    const fileName = `clientes_${statusName.toLowerCase()}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    alert(`✅ ${filteredClients.length} cliente(s) exportado(s) com sucesso!`);
  };

  const exportToExcel = () => {
    const dataToExport = filteredCobrancas.map(c => ({
      'Nome': c.customerName || c.client?.full_name || c.client?.name || '—',
      'Valor': fmt(c.value),
      'Descrição': c.description || '—',
      'Forma de Pagamento': c.billingType === 'BOLETO' ? 'Boleto Bancário' :
                           c.billingType === 'CREDIT_CARD' ? 'Cartão de Crédito' :
                           c.billingType === 'PIX' ? 'PIX' : c.billingType || '—',
      'Data de Vencimento': formatDate(c.dueDate),
      'Status': c.status === 'OVERDUE' ? 'Atrasada' :
                c.status === 'PENDING' ? 'Pendente' :
                c.status === 'RECEIVED' ? 'Recebida' : 'Confirmada'
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cobranças');

    const fileName = `cobrancas_${selectedCard || 'todas'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    setShowBulkActions(false);
  };

  const downloadBulkBoletos = async () => {
    const cobrancasToDownload = selectedCobrancas.size > 0
      ? filteredCobrancas.filter(c => selectedCobrancas.has(c.id))
      : filteredCobrancas;

    const zip = new JSZip();
    let count = 0;

    const token = localStorage.getItem('token'); 

    for (const cobranca of cobrancasToDownload) {
      try {
        const response = await fetch(`/api/asaas/download-boleto/${cobranca.id}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Falha na requisição: ${response.status}`);
        }

        const blob = await response.blob();
        
        if (blob.type !== 'application/pdf' && blob.type !== 'application/octet-stream') {
          console.warn(`⚠️ O arquivo baixado para ${cobranca.customerName} não é um PDF válido.`);
          continue;
        }

        const fileName = `boleto_${cobranca.customerName || 'cliente'}_${formatDate(cobranca.dueDate).replace(/\//g, '-')}.pdf`;
        zip.file(fileName, blob);
        count++;
        
      } catch (err) {
        console.error(`❌ Erro ao baixar boleto de ${cobranca.customerName}:`, err);
      }
    }

    if (count > 0) {
      const content = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `boletos_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      alert(`✅ ${count} boleto(s) baixado(s) com sucesso!`);
    } else {
      alert('Nenhum boleto pôde ser baixado. Verifique o console para mais detalhes.');
    }

    setShowBulkActions(false);
  };

  const printBulkBoletos = () => {
    const cobrancasToPrint = selectedCobrancas.size > 0
      ? filteredCobrancas.filter(c => selectedCobrancas.has(c.id))
      : filteredCobrancas;

    const boletosWithUrl = cobrancasToPrint.filter(c => c.invoiceUrl);

    if (boletosWithUrl.length === 0) {
      alert('Nenhum boleto disponível para impressão.');
      return;
    }

    boletosWithUrl.forEach(cobranca => {
      window.open(cobranca.invoiceUrl, '_blank');
    });

    setShowBulkActions(false);
  };

  const toggleCobrancaSelection = (id) => {
    const newSelected = new Set(selectedCobrancas);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedCobrancas(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedCobrancas.size === filteredCobrancas.length) {
      setSelectedCobrancas(new Set());
    } else {
      setSelectedCobrancas(new Set(filteredCobrancas.map(c => c.id)));
    }
  };

  return (
    <div className="situacao-cobrancas-container">
      <div className="sc-header">
        <h2 className="sc-title">Situação das Cobranças</h2>
        <div className="sc-controls">
          <div className="sc-period-selector" style={{ position: 'relative', display: 'inline-block' }}>
            <button className="sc-btn-period" onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}>
              <Calendar size={16} />
              <span>{getPeriodLabel()}</span>
              <ChevronRight size={14} className={`sc-chevron ${showPeriodDropdown ? 'rotated' : ''}`} />
            </button>

            {showPeriodDropdown && (
              <div className="sc-period-dropdown" style={{
                position: 'absolute', top: '100%', left: 0, marginTop: '8px', zIndex: 1000,
                backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '200px', padding: '8px 0'
              }}>
                <button onClick={() => handlePeriodSelect('today')}>Hoje</button>
                <button onClick={() => handlePeriodSelect('thisMonth')}>Este mês</button>
                <button onClick={() => handlePeriodSelect('thisYear')}>Este ano</button>
                <button onClick={() => handlePeriodSelect('all')}>Desde o início</button>
                <button onClick={() => handlePeriodSelect('custom')}>Personalizado</button>

                {selectedPeriod === 'custom' && (
                  <div className="sc-custom-dates">
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="sc-date-input" />
                    <span>até</span>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="sc-date-input" />
                    <div className="sc-custom-dates-buttons">
                      <button className="sc-btn-clear-dates" onClick={() => {
                        setStartDate(getFirstDayOfMonth());
                        setEndDate(getToday());
                        setSelectedPeriod('thisMonth');
                        fetchStats();
                      }}>Limpar</button>
                      <button className="sc-btn-apply-dates" onClick={handleApplyCustomDates}>Aplicar</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <button className={`sc-btn-filters ${showFilterPopover ? 'active' : ''}`} onClick={() => setShowFilterPopover(!showFilterPopover)}>
            <Filter size={16} /><span>Filtros</span>
            <ChevronRight size={14} className={`sc-chevron ${showFilterPopover ? 'rotated' : ''}`} />
          </button>

          <button className="sc-btn-refresh" onClick={handleManualRefresh} disabled={loading || syncing} title="Sincronizar dados com Asaas">
            <RefreshCw size={16} className={(loading || syncing) ? 'spinning' : ''} />
            <span className="refresh-text">Atualizar</span>
          </button>
        </div>
      </div>

      {showFilterPopover && (
        <div className="sc-filter-popover">
          <div className="sc-popover-header">
            <h4>Filtros</h4>
            <button className="sc-btn-close-popover" onClick={() => setShowFilterPopover(false)}><X size={18} /></button>
          </div>
          <div className="sc-popover-body-vertical">
            <div className="sc-filter-section">
              <h5>Filtrar por tipo de cobrança</h5>
              <label className="sc-radio-label"><input type="radio" name="billingType" value="ALL" checked={billingType === 'ALL'} onChange={(e) => setBillingType(e.target.value)} /><span>Todas</span></label>
              <label className="sc-radio-label"><input type="radio" name="billingType" value="SUBSCRIPTION" checked={billingType === 'SUBSCRIPTION'} onChange={(e) => setBillingType(e.target.value)} /><span>Assinaturas</span></label>
              <label className="sc-radio-label"><input type="radio" name="billingType" value="SINGLE" checked={billingType === 'SINGLE'} onChange={(e) => setBillingType(e.target.value)} /><span>Avulsas</span></label>
              <label className="sc-radio-label"><input type="radio" name="billingType" value="INSTALLMENT" checked={billingType === 'INSTALLMENT'} onChange={(e) => setBillingType(e.target.value)} /><span>Parceladas</span></label>
            </div>
            <div className="sc-filter-section">
              <h5>Filtrar por data</h5>
              <label className="sc-radio-label"><input type="radio" name="dateFilterType" value="dueDate" checked={dateFilterType === 'dueDate'} onChange={(e) => setDateFilterType(e.target.value)} /><span>Data de vencimento</span></label>
              <label className="sc-radio-label"><input type="radio" name="dateFilterType" value="received_date" checked={dateFilterType === 'received_date'} onChange={(e) => setDateFilterType(e.target.value)} /><span>Data de recebimento</span></label>
              <label className="sc-radio-label"><input type="radio" name="dateFilterType" value="created_date" checked={dateFilterType === 'created_date'} onChange={(e) => setDateFilterType(e.target.value)} /><span>Data de criação da cobrança</span></label>
            </div>
            <div className="sc-filter-section">
              <h5>Outras opções</h5>
              <label className="sc-checkbox-label"><input type="checkbox" checked={true} readOnly /><span>Mostrar cobranças recebidas em dinheiro</span></label>
              <label className="sc-checkbox-label"><input type="checkbox" checked={false} readOnly /><span>Ocultar cobranças antecipadas</span></label>
              <label className="sc-checkbox-label"><input type="checkbox" checked={false} readOnly /><span>Ocultar cobranças negativadas</span></label>
              <label className="sc-checkbox-label"><input type="checkbox" checked={false} readOnly /><span>Incluir previsão de assinaturas</span></label>
            </div>
          </div>
          <div className="sc-popover-footer">
            <button className="sc-btn-clear" onClick={() => { setBillingType('ALL'); setDateFilterType('dueDate'); setShowFilterPopover(false); fetchStats(); }}>Limpar</button>
            <button className="sc-btn-apply" onClick={() => { fetchStats(); if (selectedCard) fetchCobrancas(selectedCard); setShowFilterPopover(false); }}>Aplicar</button>
          </div>
        </div>
      )}

      <div className="cob-cards-grid">
        {cards.map(card => {
          const data = stats[card.key];
          const isSelected = selectedCard === card.key;
          return (
            <div key={card.key} className={`cob-card ${card.cls} ${isSelected ? 'selected' : ''}`}>
              <div className="cob-card-header">
                <span className="cob-card-label">{card.label}</span>
                <AlertCircle size={14} title={card.desc} className="info-icon" />
              </div>
              <div className="cob-card-body">
                <div className="main-value">{fmt(data.value)}</div>
                <div className="net-value">{fmt(data.netValue)} líquido</div>
                {data.count === 0 ? (
                  <div className="no-data">Não há dados para visualizar</div>
                ) : (
                  <div className="data-bar-container"><div className="data-bar" style={{ width: '100%' }}></div></div>
                )}
              </div>
              <div className="cob-card-footer">
                <div className="footer-item" style={{ cursor: 'pointer' }} onClick={() => handleFooterClick('clients', card.key)}>
                  <Users size={14} /><span>{data.customers} cliente{data.customers !== 1 ? 's' : ''}</span><ChevronRight size={14} />
                </div>
                <div className="footer-item" style={{ cursor: 'pointer' }} onClick={() => handleCardClick(card.key)}>
                  <DollarSign size={14} /><span>{data.count} cobrança{data.count !== 1 ? 's' : ''}</span>
                  <ChevronRight size={14} className={isSelected ? 'rotated' : ''} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selectedCard && (
        <div className="cobrancas-list-section">
          <div className="cobrancas-list-header">
            <div className="cobrancas-list-title">
              <h3>{cards.find(c => c.key === selectedCard)?.label}</h3>
              <span className="cobrancas-count">{filteredCobrancas.length} cobrança{filteredCobrancas.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="cobrancas-list-actions">
              <div className="search-box">
                <Search size={16} />
                <input type="text" placeholder="Procurar por nome ou email do cliente" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
              
              <div style={{ position: 'relative' }}>
                <button 
                  className="btn-bulk-actions"
                  onClick={() => setShowBulkActions(!showBulkActions)}
                >
                  <Download size={16} />
                  <span>Ações em Lote</span>
                  <ChevronRight size={14} className={`sc-chevron ${showBulkActions ? 'rotated' : ''}`} />
                </button>
                
                {showBulkActions && (
                  <div className="bulk-actions-dropdown">
                    <button onClick={exportToExcel}>
                      <FileSpreadsheet size={16} />
                      Exportar dados
                    </button>
                    <button onClick={downloadBulkBoletos}>
                      <Download size={16} />
                      Baixar boletos {selectedCobrancas.size > 0 && `(${selectedCobrancas.size})`}
                    </button>
                    <button onClick={printBulkBoletos}>
                      <Printer size={16} />
                      Imprimir boletos {selectedCobrancas.size > 0 && `(${selectedCobrancas.size})`}
                    </button>
                  </div>
                )}
              </div>
              
              <button className="btn-close-list" onClick={() => setSelectedCard(null)}><X size={16} /> Fechar</button>
            </div>
          </div>

          {loadingCobrancas ? (
            <div className="loading-cobrancas"><RefreshCw size={24} className="spinning" /><p>Carregando cobranças...</p></div>
          ) : filteredCobrancas.length === 0 ? (
            <div className="no-cobrancas"><FileText size={48} /><p>Nenhuma cobrança encontrada</p></div>
          ) : (
            <div className="cobrancas-table-wrapper">
              <table className="cobrancas-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input 
                        type="checkbox" 
                        checked={selectedCobrancas.size === filteredCobrancas.length && filteredCobrancas.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th>Nome</th>
                    <th>Valor</th>
                    <th>Descrição</th>
                    <th>Forma de pagamento</th>
                    <th>Data de vencimento</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCobrancas.map((cobranca) => (
                    <tr key={cobranca.id} className={selectedCobrancas.has(cobranca.id) ? 'selected' : ''}>
                      <td>
                        <input 
                          type="checkbox" 
                          checked={selectedCobrancas.has(cobranca.id)}
                          onChange={() => toggleCobrancaSelection(cobranca.id)}
                        />
                      </td>
                      <td><div className="client-info"><Users size={16} /><span>{cobranca.customerName || cobranca.client?.full_name || cobranca.client?.name || '—'}</span></div></td>
                      <td className="value-cell">{fmt(cobranca.value)}</td>
                      <td>{cobranca.description || '—'}</td>
                      <td><span className={`billing-type-badge billing-${cobranca.billingType?.toLowerCase()}`}>
                        {cobranca.billingType === 'BOLETO' ? 'Boleto Bancário' :
                         cobranca.billingType === 'CREDIT_CARD' ? 'Cartão de Crédito' :
                         cobranca.billingType === 'PIX' ? 'PIX' : cobranca.billingType || '—'}
                      </span></td>
                      <td><div className="due-date">{formatDate(cobranca.dueDate)}{cobranca.status === 'OVERDUE' && <span className="overdue-badge">Atrasada</span>}</div></td>
                      <td>
                        <div className="actions-buttons">
                          {cobranca.invoiceUrl && <a href={cobranca.invoiceUrl} target="_blank" rel="noopener noreferrer" className="action-btn"><FileText size={16} /></a>}
                          {cobranca.bankSlipUrl && <a href={cobranca.bankSlipUrl} target="_blank" rel="noopener noreferrer" className="action-btn"><DollarSign size={16} /></a>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ✅ SEÇÃO DE CLIENTES - COM BUSCA E EXPORTAÇÃO FUNCIONANDO */}
      {selectedClientCard && (
        <div className="cobrancas-list-section">
          <div className="cobrancas-list-header">
            <div className="cobrancas-list-title">
              <h3>
                {selectedClientCard === 'RECEIVED' && 'Clientes Adimplentes'}
                {selectedClientCard === 'CONFIRMED' && 'Clientes Confirmados'}
                {selectedClientCard === 'PENDING' && 'Clientes Pendentes'}
                {selectedClientCard === 'OVERDUE' && 'Clientes Inadimplentes'}
              </h3>
              <span className="cobrancas-count">
                {filteredClients.length} de {clients.length} clientes
              </span>
            </div>
            <div className="cobrancas-list-actions">
              {/* BUSCA DE CLIENTES - COM KEY */}
<div className="search-box" style={{ flex: 1, maxWidth: '400px' }}>
  <Search size={16} />
  <input 
    key={`client-search-${selectedClientCard}`}
    type="text" 
    placeholder="Procurar por nome, email, CPF ou CNPJ" 
    value={clientSearchTerm}
    onChange={(e) => setClientSearchTerm(e.target.value)}
    style={{ width: '100%', outline: 'none', border: 'none', background: 'transparent' }}
  />
</div>
              
              {/* BOTÃO EXPORTAR CLIENTES */}
              <button 
                className="btn-export-clients" 
                onClick={() => {
                  console.log('📥 Exportando clientes...');
                  exportClientsToExcel();
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => e.target.style.background = '#059669'}
                onMouseLeave={(e) => e.target.style.background = '#10b981'}
              >
                <FileSpreadsheet size={16} />
                Exportar Excel
              </button>
              
              <button className="btn-close-list" onClick={() => { 
                setSelectedClientCard(null); 
                setClientSearchTerm(''); 
                setClients([]);
              }}>
                <X size={16} /> Fechar
              </button>
            </div>
          </div>

          {loadingClients ? (
            <div className="loading-cobrancas"><RefreshCw size={24} className="spinning" /><p>Carregando clientes...</p></div>
          ) : filteredClients.length === 0 ? (
            <div className="no-cobrancas">
              <Users size={48} />
              <p>
                {clientSearchTerm 
                  ? 'Nenhum cliente encontrado para esta busca' 
                  : 'Nenhum cliente encontrado'}
              </p>
            </div>
          ) : (
            <div className="cobrancas-table-wrapper">
              <table className="cobrancas-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Empresa</th>
                    <th>Assinaturas recebidas</th>
                    <th>Cobranças recebidas</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.id}>
                      <td>
                        <div className="client-info">
                          <div className="avatar">
                            {(client.full_name || client.name || 'C').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="name">{client.full_name || client.name}</div>
                            <div className="client-details">
                              {client.email && <div className="detail-item"><Mail size={12} />{client.email}</div>}
                              {client.phone && <div className="detail-item"><Phone size={12} />{client.phone}</div>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{client.cpf || '—'}</td>
                      <td>{fmt(client.total_assinaturas)}</td>
                      <td>{fmt(client.total_cobrancas)}</td>
                      <td>
                        <div className="actions-buttons">
                          <button className="action-btn"><Edit2 size={14} /></button>
                          <button className="action-btn"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
